import {
  useContext, useEffect, useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Divider from '@mui/material/Divider';
import Chip, { ChipProps } from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';

import {
  useGetBulkOrdersQuery,
} from '#services/order';
import {
  formatDate,
  formatCurrency,
} from '#common/utils';
import {
  FULFILLMENT_STATUS,
  FULFILLMENT_COLOR,
  FULFILLMENT_LITERAL,
} from '#common/constants';
import URLSearchParamsEx from '#utils/url-ex';
import EnhancedTable, { EnhancedTableProps } from '#components/EnhancedTable';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  resolveDateRangeParam,
  toDateRangeQueryParams,
} from '#components/time-range.utils';
import DropdownFilterBox from '#components/DropdownFilterBox';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  setDateRange,
  setFilterKey,
  setFilter,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setSelected,
} from './bulk.slice';

interface BulkListSlice {
  dateRange: TimeRangeValue;
  filter: string;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  pageSize: number;
  rows: EnhancedTableProps['rows'];
  rowCount: number;
  selected: Array<string | number | undefined>;
  vendors: Record<string, string>;
  skip: boolean;
}

interface BulkRow {
  id?: string | number;
  createdByUserName?: string;
  createdByUserCode?: string;
  createdDate?: string;
  availableDate?: string;
  receivings?: Array<string | number>;
  totalPrice?: number;
  orderStatus?: number;
  [key: string]: unknown;
}

export default function AdminBulkList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const filterKey = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.filterKey);
  const filter = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.filter);
  const dateRange = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.dateRange);
  const headerFilters = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.headerFilters);
  const order = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.order);
  const orderBy = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.orderBy);
  const pageIndex = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.pageIndex);
  const pageSize = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.pageSize);
  const rows = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.rows);
  const rowCount = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.rowCount);
  const selected = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.selected);
  const vendors = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.vendors);
  const skip = useSelector((state: { bulkOrderList: BulkListSlice }) => state.bulkOrderList.skip);
  const [debouncedPageIndex] = useDebounce(pageIndex, 300);
  const [filterForQuery, setFilterForQuery] = useState(filter);
  const [isInitialMount, setIsInitialMount] = useState(true);

  const {
    showModal,
    hideModal,
    setModalData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const dateRangeParam = resolveDateRangeParam('createdDate', dateRange);

  const query = useGetBulkOrdersQuery({
    dateRange: dateRangeParam,
    order,
    orderBy,
    filterKey,
    filter: filterForQuery,
    headerFilters,
    pageIndex: debouncedPageIndex,
    pageSize,
  }, {
    refetchOnMountOrArgChange: true,
    skip,
  });

  // Restore state from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      setFilterForQuery(urlParams.values.filter as string);
    }
    if (urlParams.values.filterKey !== undefined) {
      dispatch(setFilterKey(urlParams.values.filterKey as string));
    }
    if (urlParams.values.headerFilters !== undefined) {
      dispatch(setHeaderFilters(urlParams.values.headerFilters as Record<string, Record<string, boolean>>));
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }
    if (urlParams.values.pageSize !== undefined) {
      dispatch(setPageSize(urlParams.values.pageSize as number));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  // Sync filterForQuery with filter when filter changes
  useEffect(() => {
    setFilterForQuery(filter);
  }, [filter]);

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  const handleBulkOrdersExport = () => {
    // WORKAROUND react state cannot work, use local variable instead
    let exportTargetOption = 'current';

    setModalData({
      children: (
        <>
          <Typography variant="h6" gutterBottom>
            Export Orders
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControl sx={{ mb: 5 }}>
            <FormLabel id="label-export-radio">Export</FormLabel>
            <RadioGroup
              aria-labelledby="label-export-radio"
              defaultValue="current"
              name="radio-buttons-group"
              onChange={(event, value) => {
                exportTargetOption = value;
              }}
            >
              <FormControlLabel value="current" control={<Radio />} label="Current page" />
              <FormControlLabel value="all" control={<Radio />} label="All orders" />
              <FormControlLabel
                disabled={!selected.length}
                value="selected"
                control={<Radio />}
                label={`Selected: ${selected.length} bulk orders`}
              />
            </RadioGroup>
          </FormControl>

          <Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={hideModal}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                let additionalParam = '';
                if (exportTargetOption === 'current') {
                  additionalParam = `order=${order}&orderBy=${orderBy}&filter=${filter}&page=${pageIndex}&pageSize=${pageSize}`;
                } else if (exportTargetOption === 'all') {
                  additionalParam = `order=${order}&orderBy=${orderBy}&filter=${filter}&all=1`;
                } else if (exportTargetOption === 'selected') {
                  additionalParam = `bulkOrderIds=${selected.join(',')}`;
                }

                // An explicit selection is already the exact row set the user
                // picked, so only the page/all exports carry the table's range.
                if (exportTargetOption !== 'selected') {
                  const rangeParams = new URLSearchParams(
                    toDateRangeQueryParams(dateRangeParam),
                  ).toString();
                  if (rangeParams) {
                    additionalParam = `${additionalParam}&${rangeParams}`;
                  }
                }

                const exportUrl = `/api/export/bulkOrders?${additionalParam}`;
                window.open(exportUrl, '_blank');
                hideModal();
              }}
            >
              Export orders
            </Button>
          </Stack>
        </>
      ),
      style: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
      },
    });
    showModal();
  };

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'id',
    label: 'Order #',
    renderer: (row, idx) => {
      const bulkRow = row as BulkRow;
      const bulkOrderIndex = pageIndex * pageSize + idx + 1;
      const qs = new URLSearchParamsEx({
        order,
        orderBy,
        filter,
        filterKey,
        headerFilters,
        bulkOrderId: bulkRow.id,
        bulkOrderIndex,
        // The detail page re-runs the list query to resolve the row index (and
        // to walk prev/next), so it needs the same date window the list used —
        // without it the index points into the unfiltered list.
        ...(dateRangeParam ? { dateRange: dateRangeParam } : {}),
      }).toString();
      const url = `/management/bulk/detail?${qs}`;
      return (
        <Link
          href={url}
          onClick={(e) => {
            e.preventDefault();
            void navigate(url);
          }}
        >
          {bulkRow.id}
        </Link>
      );
    },
  }, {
    id: 'createdByUserName',
    label: 'Created By',
    renderer: (r) => `${(r as BulkRow).createdByUserName}#${(r as BulkRow).createdByUserCode}`,
  }, {
    id: 'vendor',
    label: 'Vendor',
    filters: vendors,
  }, {
    id: 'createdDate',
    label: 'Created Date',
    renderer: (o) => {
      try {
        const { createdDate } = o as BulkRow;
        return formatDate(createdDate as unknown as Parameters<typeof formatDate>[0]);
      } catch (_err) {
        return 'UNAVAILABLE';
      }
    },
  }, {
    id: 'availableDate',
    label: 'Available Date',
    renderer: (o) => {
      const { availableDate } = o as BulkRow;
      if (!availableDate) {
        return '-';
      }

      try {
        return formatDate(availableDate);
      } catch (_err) {
        return 'UNAVAILABLE';
      }
    },
  }, {
    id: 'receivings',
    label: 'Receiving List',
    sortable: false,
    renderer: (o) => {
      const { receivings } = o as BulkRow;
      return (receivings || []).map((receivingId) => {
        const url = `/management/receiving/edit?receivingId=${receivingId}`;
        return (
          <Link
            key={receivingId}
            href={url}
            onClick={(e) => {
              e.preventDefault();
              void navigate(url);
            }}
            sx={{
              ml: 0.5,
              textDecoration: 'inherit',
            }}
          >
            {receivingId}
          </Link>
        );
      });
    },
  }, {
    id: 'totalPrice',
    label: 'Order Total',
    renderer: (o) => `${formatCurrency((o as BulkRow).totalPrice as unknown as Parameters<typeof formatCurrency>[0])}`,
  }, {
    id: 'orderStatus',
    label: 'Order Status',
    filters: FULFILLMENT_LITERAL,
    renderer: (o) => {
      const status = (o as BulkRow).orderStatus as number;
      return (
        <Chip
          size="small"
          label={FULFILLMENT_LITERAL[status]}
          color={FULFILLMENT_COLOR[status] as ChipProps['color']}
        />
      );
    },
  }];

  const tableProps: EnhancedTableProps = {
    name: 'bulk-list',

    headers: tableHeaders,

    enableSort: true,
    order,
    setOrder,
    orderBy,
    setOrderBy,

    headerFilters,
    setHeaderFilters,
    pinnedColumns: {
      left: ['id'],
    },
    getRowStyle: (row) => (
      ((row as BulkRow).orderStatus === FULFILLMENT_STATUS.CANCELED)
        ? { textDecoration: 'line-through' }
        : {}
    ),

    hasSelectColumn: true,
    selected,
    setSelected,

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,

    timeRange: {
      value: dateRange,
      onChange: (value) => dispatch(setDateRange(value)),
      columnLabel: 'Created Date',
      defaultPreset: DEFAULT_TIME_RANGE_PRESET,
      entityLabel: 'bulk orders',
    },

    rows,
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Bulk Orders
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <DropdownFilterBox
          width={500}
          dropdownItems={[{
            value: 'bulkOrderId',
            text: 'Bulk Order #',
          }, {
            value: 'orderId',
            text: 'Customer Order #',
          }, {
            value: 'style',
            text: 'Style #',
          }]}
          filter={filter}
          filterKey={filterKey}
          filterPlaceholder="Filter orders"
          dispatch={dispatch as (action: unknown) => void}
          setFilter={setFilter}
          setFilterKey={setFilterKey}
          setFilterForQuery={setFilterForQuery}
        />

        <ButtonGroup variant="outlined" size="small">
          <Button
            onClick={handleBulkOrdersExport}
          >
            Export
          </Button>
        </ButtonGroup>
      </Box>

      <EnhancedTable
        {...tableProps}
      />
    </>
  );
}
